/** Magnetic stick flags — port of SwingOverlayPage STICK_* constants. */
export const MAGNETIC_TOP = 0x0001
export const MAGNETIC_RIGHT = 0x0010
export const MAGNETIC_BOTTOM = 0x0100
export const MAGNETIC_LEFT = 0x1000
export const STICK_OFFSET = 15

export interface Rect {
  x: number
  y: number
  width: number
  height: number
}

export interface Size {
  width: number
  height: number
}

export interface DefBounds {
  posX: number
  posY: number
  width: number
  height: number
}

/** Swing reInitFormBounds: anchor at form top-left in layer coords, extends to layer right/bottom. */
export function calcSwingOverlayHostRect(
  anchorEl: HTMLElement | null,
  layerEl: HTMLElement | null,
): Rect {
  if (!anchorEl || !layerEl) {
    const fallback = anchorEl ?? layerEl
    if (!fallback) return { x: 0, y: 0, width: 0, height: 0 }
    return { x: 0, y: 0, width: fallback.clientWidth, height: fallback.clientHeight }
  }

  const anchor = anchorEl.getBoundingClientRect()
  const layer = layerEl.getBoundingClientRect()
  const x = anchor.left - layer.left
  const y = anchor.top - layer.top
  return {
    x,
    y,
    width: layer.width + layer.left - anchor.left,
    height: layer.height + layer.top - anchor.top,
  }
}

/** Initial overlay bounds from XML defBounds + host + preferred size (SwingOverlayPage hierarchy listener). */
export function calcInitialBounds(host: Rect, def: DefBounds, preferred: Size): Rect {
  const width = def.width > 0 ? def.width : preferred.width
  const height = def.height > 0 ? def.height : preferred.height

  let x =
    def.posX < 0
      ? host.x + host.width + def.posX - width
      : host.x + def.posX
  let y =
    def.posY < 0
      ? host.y + host.height + def.posY - height
      : host.y + def.posY

  if (width > host.width) x = host.x
  if (height > host.height) y = host.y

  return { x, y, width, height }
}

/** Update magnetic stick flags from overlay position relative to host (SwingOverlayPage.updateMagnetic). */
export function updateMagnetic(host: Rect, overlayBounds: Rect, _magnetic = 0): number {
  let magnetic = 0
  if (host.width > overlayBounds.width) {
    if (overlayBounds.x < host.x + STICK_OFFSET) {
      magnetic |= MAGNETIC_LEFT
    } else if (overlayBounds.x + overlayBounds.width + STICK_OFFSET > host.x + host.width) {
      magnetic |= MAGNETIC_RIGHT
    }
  }
  if (host.height > overlayBounds.height) {
    if (overlayBounds.y < host.y + STICK_OFFSET) {
      magnetic |= MAGNETIC_TOP
    } else if (overlayBounds.y + overlayBounds.height + STICK_OFFSET > host.y + host.height) {
      magnetic |= MAGNETIC_BOTTOM
    }
  }
  return magnetic
}

/** Recalculate overlay location on host resize (SwingOverlayPage.calcOverlayLocation). */
export function calcOverlayLocation(
  host: Rect,
  overlayLocation: { x: number; y: number },
  preferred: Size,
  magnetic: number,
): { x: number; y: number } {
  let x: number
  if ((magnetic & MAGNETIC_LEFT) === MAGNETIC_LEFT) {
    x = host.x
  } else if ((magnetic & MAGNETIC_RIGHT) === MAGNETIC_RIGHT) {
    x = host.x + host.width - preferred.width
  } else {
    x =
      overlayLocation.x + preferred.width > host.x + host.width
        ? host.x + host.width - preferred.width
        : overlayLocation.x
  }
  if (preferred.width > host.width) x = host.x

  let y: number
  if ((magnetic & MAGNETIC_TOP) === MAGNETIC_TOP) {
    y = host.y
  } else if ((magnetic & MAGNETIC_BOTTOM) === MAGNETIC_BOTTOM) {
    y = host.y + host.height - preferred.height
  } else {
    y =
      overlayLocation.y + preferred.height > host.y + host.height
        ? host.y + host.height - preferred.height
        : overlayLocation.y
  }
  if (preferred.height > host.height) y = host.y

  return { x, y }
}

/** Clamp drag delta and update magnetic (SwingOverlayPage.OverlayMover.move). */
export function clampDragMove(
  host: Rect,
  currentLocation: { x: number; y: number },
  preferred: Size,
  delta: { dx: number; dy: number },
  magnetic: number,
): { x: number; y: number; magnetic: number } {
  let newX = currentLocation.x + delta.dx
  let newY = currentLocation.y + delta.dy

  const rightBound = host.width + host.x
  const bottomBound = host.height + host.y

  if (preferred.width <= host.width) {
    if (newX + preferred.width + STICK_OFFSET > rightBound) {
      newX = rightBound - preferred.width
    } else if (newX - STICK_OFFSET < host.x) {
      newX = host.x
    }
  }
  if (newX < host.x) newX = host.x

  if (preferred.height <= host.height) {
    if (newY - STICK_OFFSET < host.y) {
      newY = host.y
    } else if (newY + preferred.height + STICK_OFFSET > bottomBound) {
      newY = bottomBound - preferred.height
    }
  }
  if (newY < host.y) newY = host.y

  const bounds: Rect = {
    x: newX,
    y: newY,
    width: preferred.width,
    height: preferred.height,
  }
  const newMagnetic = updateMagnetic(host, bounds, magnetic)

  return { x: newX, y: newY, magnetic: newMagnetic }
}
